import React, { useState, useEffect, useCallback, useRef, useMemo, Activity } from "react";
import styled from "styled-components";

import TopAppBarHome from "../../components/top-app-bar/TopAppBarHome";
import SvgLoadingAnimation from "../../components/loading";

import {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  Project,
  ACTIVE_PROJECTS_LIMIT,
} from "../../app/services/projects";

import FilterChipBar from "../../components/filter-chip/FilterChipBar";
import { useObservedHeight } from "../../hooks/useObservedHeight";
import ProjectWheel from "./components/ProjectWheel";

// ── Types ──

/** `/projects` ページ内のビュー。タスクボードはグローバルメニュー TASK（`/taskboard`）。 */
type ViewMode = "in_progress" | "completed" | "search";

// ── Constants ──

/**
 * 検索タブもホイールで一度に並べるため、API の上限件数（最大 100 件）で取得する。
 */
const SEARCH_RESULTS_LIMIT = 100;
/**
 * 完了タブも進行中と同じホイール（全件を一度に並べる）。年チップで絞り込んだ分を
 * API の上限件数ずつ取得し、足りない分は eager ページングで追い読みする。
 */
const COMPLETED_PAGE_LIMIT = 100;
/** 完了がこの件数未満のユーザーだけ「すべて」チップを出し、初期表示もすべてにする */
const COMPLETED_ALL_CHIP_THRESHOLD = 10;
/** 年チップの「すべて」選択 */
const ALL_YEARS = "all";
type CompletedYearSelection = number | typeof ALL_YEARS;

// ── Styled Components ──

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 64px 16px;
  width: 100%;
`;

// ── Main Component ──

const Projects: React.FC = () => {

  // ── ビューモード ──
  const initialViewMode: ViewMode = "in_progress";
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [visitedModes, setVisitedModes] = useState<Set<ViewMode>>(
    new Set([initialViewMode]),
  );
  const prevViewModeRef = useRef<ViewMode>(initialViewMode);

  // ── ページネーション用 state（進行中） ──
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [activeHasMore, setActiveHasMore] = useState(false);
  const [activeOffset, setActiveOffset] = useState(0);
  const [activeLoadingMore, setActiveLoadingMore] = useState(false);
  const activeInitialRef = useRef(true);

  // ── 完了: 年チップの選択（null = 未操作。既定値は下で算出） ──
  const [completedYear, setCompletedYear] = useState<CompletedYearSelection | null>(null);

  // ── 検索 ──
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── API クエリ ──
  const {
    data: activeData,
    isLoading: activeLoading,
    error: activeError,
  } = useGetProjectsQuery({ status: "active", limit: ACTIVE_PROJECTS_LIMIT });

  // ── 完了: 年チップ（件数は進行中の一覧レスポンスに同梱されている） ──
  const completedYearCounts = useMemo(
    () => activeData?.data?.completed_year_counts ?? [],
    [activeData],
  );
  const showAllYearsChip =
    (activeData?.data?.total_completed_count ?? 0) < COMPLETED_ALL_CHIP_THRESHOLD;
  const defaultCompletedYear: CompletedYearSelection = showAllYearsChip
    ? ALL_YEARS
    : completedYearCounts[0]?.year ?? ALL_YEARS;
  // 選択中の年が一覧から消えた（完了が進行中へ戻った等）ときは既定値へ戻す
  const effectiveCompletedYear: CompletedYearSelection =
    completedYear === null ||
    (completedYear === ALL_YEARS
      ? !showAllYearsChip
      : !completedYearCounts.some((y) => y.year === completedYear))
      ? defaultCompletedYear
      : completedYear;
  const completedYearParam =
    effectiveCompletedYear === ALL_YEARS ? undefined : effectiveCompletedYear;

  const {
    currentData: completedData,
    isFetching: completedFetching,
    error: completedError,
  } = useGetProjectsQuery(
    { status: "completed", limit: COMPLETED_PAGE_LIMIT, year: completedYearParam },
    { skip: !visitedModes.has("completed") }
  );

  const {
    data: searchData,
    isLoading: searchLoading,
  } = useGetProjectsQuery(
    { status: "all", search: debouncedSearchText, limit: SEARCH_RESULTS_LIMIT },
    { skip: viewMode !== "search" || !debouncedSearchText.trim() }
  );

  const [fetchMoreProjects] = useLazyGetProjectsQuery();

  // タブの存在判定は active レスポンスに含まれる total_*_count を参照する。
  const hasInProgressProjects = (activeData?.data?.total_active_count ?? 0) > 0;
  const hasCompletedProjects = (activeData?.data?.total_completed_count ?? 0) > 0;

  const projectFilterOptions = useMemo(() => {
    if (activeLoading) {
      return [
        { label: "進行中", value: "in_progress" as ViewMode },
        { label: "完了", value: "completed" as ViewMode },
      ];
    }
    const options: { label: string; value: ViewMode }[] = [];
    if (hasInProgressProjects) {
      options.push({ label: "進行中", value: "in_progress" });
    }
    if (hasCompletedProjects) {
      options.push({ label: "完了", value: "completed" });
    }
    return options;
  }, [activeLoading, hasInProgressProjects, hasCompletedProjects]);

  const firstAvailableMode = projectFilterOptions[0]?.value ?? "in_progress";

  // ── 進行中: 初回データの初期化 ──
  useEffect(() => {
    if (!activeData?.data) return;
    const fresh = activeData.data.projects || [];
    if (activeInitialRef.current) {
      setActiveProjects(fresh);
      setActiveHasMore(activeData.data.has_next);
      setActiveOffset(activeData.data.next_offset ?? 0);
      activeInitialRef.current = false;
    } else {
      setActiveProjects(prev => {
        const freshIds = new Set(fresh.map(p => p.project_id));
        const paginated = prev.filter(p => !freshIds.has(p.project_id));
        return [...fresh, ...paginated];
      });
    }
  }, [activeData]);

  // ── 完了: 選択中の年の全件 eager 先読み ──
  // ホイールは全カードを一度に並べるため、has_next が尽きるまで追い読みする。
  // 結果は年ごとのキーで保持し、年を切り替えた直後に前の年の分が混ざらないようにする。
  const completedKey = String(effectiveCompletedYear);
  const [completedExtra, setCompletedExtra] = useState<{
    key: string;
    projects: Project[];
  } | null>(null);
  useEffect(() => {
    const firstPage = completedData?.data;
    if (!firstPage?.has_next || firstPage.next_offset == null) return;
    let cancelled = false;
    const acc: Project[] = [];
    (async () => {
      let offset: number | null = firstPage.next_offset;
      while (offset !== null && !cancelled) {
        const result = await fetchMoreProjects({
          status: "completed",
          limit: COMPLETED_PAGE_LIMIT,
          year: completedYearParam,
          offset,
        }).unwrap();
        acc.push(...(result?.data?.projects ?? []));
        offset = result?.data?.has_next ? result.data.next_offset : null;
      }
    })()
      .catch((err) => {
        // 途中で失敗しても読み込めた分で表示する（spinner のまま止めない・再試行しない）
        console.error("[loadMoreCompleted] エラー:", err);
      })
      .finally(() => {
        if (!cancelled) setCompletedExtra({ key: completedKey, projects: acc });
      });
    return () => {
      cancelled = true;
    };
  }, [completedData, completedKey, completedYearParam, fetchMoreProjects]);

  const completedLoadingMore =
    !!completedData?.data?.has_next && completedExtra?.key !== completedKey;
  const completedProjects = useMemo(() => {
    const firstPage = completedData?.data?.projects ?? [];
    const extra =
      completedData?.data?.has_next && completedExtra?.key === completedKey
        ? completedExtra.projects
        : [];
    const seen = new Set(firstPage.map((p) => p.project_id));
    return [...firstPage, ...extra.filter((p) => !seen.has(p.project_id))];
  }, [completedData, completedExtra, completedKey]);

  // ── 完了: 年チップの高さ（ホイールの上部クロームに加える） ──
  const { ref: yearChipBarRef, height: yearChipBarHeight } =
    useObservedHeight<HTMLDivElement>();
  const yearChipItems = useMemo(
    () =>
      completedYearCounts.map((y) => ({ id: String(y.year), name: `${y.year}年` })),
    [completedYearCounts],
  );
  const handleSelectYear = useCallback((id: string | null) => {
    setCompletedYear(id === null ? ALL_YEARS : Number(id));
  }, []);

  // ── 進行中: 追加ページ読み込み ──
  const loadMoreActive = useCallback(async () => {
    if (!activeHasMore || activeLoadingMore) return;
    setActiveLoadingMore(true);
    try {
      const result = await fetchMoreProjects({
        status: "active",
        offset: activeOffset,
      }).unwrap();
      if (result?.data?.projects?.length > 0) {
        setActiveProjects(prev => {
          const existingIds = new Set(prev.map(p => p.project_id));
          const unique = result.data.projects.filter(p => !existingIds.has(p.project_id));
          return [...prev, ...unique];
        });
        setActiveHasMore(result.data.has_next);
        setActiveOffset(result.data.next_offset ?? 0);
      } else {
        setActiveHasMore(false);
      }
    } catch (err) {
      // 追い読みを止めて読み込めた分だけで表示する。止めないと下の eager
      // 先読み effect が失敗のたびに即時再試行し、障害時にリクエストが増え続ける。
      console.error("[loadMoreActive] エラー:", err);
      setActiveHasMore(false);
    } finally {
      setActiveLoadingMore(false);
    }
  }, [activeHasMore, activeLoadingMore, activeOffset, fetchMoreProjects]);

  // ── 進行中: 全件 eager 先読み ──
  // ホイール UI（ProjectWheel）は全カードを一度に円環へ並べるため、
  // スクロール到達を待つのではなく has_next が尽きるまで追い読みする。
  useEffect(() => {
    if (activeHasMore && !activeLoadingMore) {
      loadMoreActive();
    }
  }, [activeHasMore, activeLoadingMore, loadMoreActive]);

  // ── 検索テキストのデバウンス処理 ──
  const handleSearchTextChange = useCallback((text: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchText(text);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // ── セグメントコントロールの切り替え ──
  const handleViewModeChange = useCallback(
    (value: string) => {
      const mode = value as ViewMode;
      setViewMode(mode);
      if (mode !== "search") {
        prevViewModeRef.current = mode;
        setVisitedModes((prev) => new Set([...prev, mode]));
      }
    },
    [],
  );

  const availableValues = useMemo(
    () => projectFilterOptions.map((o) => o.value),
    [projectFilterOptions],
  );

  useEffect(() => {
    if (activeLoading) return;
    if (viewMode === "search") return;
    if (availableValues.includes(viewMode)) return;
    setViewMode(firstAvailableMode);
    setVisitedModes((prev) => new Set([...prev, firstAvailableMode]));
    prevViewModeRef.current = firstAvailableMode;
  }, [activeLoading, viewMode, availableValues, firstAvailableMode]);

  // 検索結果が実際にある場合のみ検索タブを表示し、それ以外は前のタブを維持
  const searchResults = searchData?.data?.projects || [];
  const hasSearchResults =
    !!debouncedSearchText.trim() && !searchLoading && searchResults.length > 0;
  const effectiveViewMode = (viewMode === "search" && !hasSearchResults)
    ? prevViewModeRef.current
    : viewMode;

  // ── 初回ローディング ──
  if (activeLoading) return <SvgLoadingAnimation />;
  if (activeError) return <div>エラーが発生しました</div>;

  return (
    <div className="u-col-center">
      {/* セグメント行は wrapper の外（全幅 + 自身の padding 16px）に置く（プロジェクト内と共通） */}
      <TopAppBarHome
        options={projectFilterOptions}
        defaultValue={firstAvailableMode}
        value={viewMode}
        onChange={handleViewModeChange}
        showSearchInput={true}
        onSearchChange={handleSearchTextChange}
        searchPlaceholder="プロジェクト名で検索..."
      />
      <div className="u-wrapper-full">
        {/* 進行中タブ: オファー作成と同じスクロール演出のホイール表示。
            全件を円環に並べるため、eager 先読みが終わるまでは spinner を出す。 */}
        <Activity mode={effectiveViewMode === "in_progress" ? "visible" : "hidden"}>
          {activeProjects.length > 0 ? (
            activeHasMore || activeLoadingMore ? (
              <div className="u-col-center u-w100 u-mt16">
                <SvgLoadingAnimation />
              </div>
            ) : (
              <ProjectWheel
                projects={activeProjects}
                active={effectiveViewMode === "in_progress"}
              />
            )
          ) : (
            <EmptyStateWrapper>
              <span className="material-symbols-rounded" style={{ fontSize: '48px', color: 'var(--soremo-light-gray)' }}>
                folder_off
              </span>
              <p className="bodytext-13 u-text-light-gray">進行中のプロジェクトはありません</p>
            </EmptyStateWrapper>
          )}
        </Activity>

        {/* 完了タブ: 進行中と同じホイール。セグメントの下の年チップで絞り込む
            （オファー作成のロール選択と同じ FilterChipBar）。 */}
        {visitedModes.has("completed") && (
          <Activity mode={effectiveViewMode === "completed" ? "visible" : "hidden"}>
            {yearChipItems.length > 0 && (
              <div ref={yearChipBarRef}>
                <FilterChipBar
                  items={yearChipItems}
                  selectedId={
                    effectiveCompletedYear === ALL_YEARS
                      ? null
                      : String(effectiveCompletedYear)
                  }
                  onSelect={handleSelectYear}
                  showAllChip={showAllYearsChip}
                  align="center"
                />
              </div>
            )}
            {completedError && !completedData ? (
              <div>エラーが発生しました</div>
            ) : (!completedData && completedFetching) || completedLoadingMore ? (
              <div className="u-col-center u-w100 u-mt16">
                <SvgLoadingAnimation />
              </div>
            ) : completedProjects.length > 0 ? (
              <ProjectWheel
                key={`completed-${completedKey}`}
                projects={completedProjects}
                active={effectiveViewMode === "completed"}
                showNewProjectCard={false}
                topInsetPx={yearChipBarHeight}
                ariaLabel="完了したプロジェクトを選択"
              />
            ) : (
              <EmptyStateWrapper>
                <span className="material-symbols-rounded" style={{ fontSize: '48px', color: 'var(--soremo-light-gray)' }}>
                  folder_off
                </span>
                <p className="bodytext-13 u-text-light-gray">完了したプロジェクトはありません</p>
              </EmptyStateWrapper>
            )}
          </Activity>
        )}

        {/* 検索タブ: 進行中 / 完了と同じホイール（進行中・完了をまとめて検索）。
            検索語ごとに作り直し、選択とスクロール位置を先頭に戻す */}
        {hasSearchResults && (
          <Activity mode={effectiveViewMode === "search" ? "visible" : "hidden"}>
            <ProjectWheel
              key={`search-${debouncedSearchText}`}
              projects={searchResults}
              active={effectiveViewMode === "search"}
              showNewProjectCard={false}
              ariaLabel="検索結果のプロジェクトを選択"
            />
          </Activity>
        )}
      </div>
    </div>
  );
};

export default Projects;
